/**
 * PermitsList Component
 * Main container for permit management with React Query
 * Phase 6 Batch 2 - Permits Frontend UI (Gold Standard Implementation)
 * Phase 8.2 - Added permit renewal support
 *
 * This component follows the recommended React Query pattern:
 * - Hooks in src/hooks/usePermits.ts (not inline mutations)
 * - Proper cache invalidation with prefix matching
 * - Optimistic updates for delete operations
 * - Testable with MSW (HTTP interception)
 *
 * @see docs/patterns/frontend/REACT_QUERY_PATTERNS.md
 * @see docs/OPTIMIZATION_ROADMAP.md - React Query Pattern Standardization
 */
import { useState, useMemo } from 'react';
import { Permit } from '@/types/permits';
import { PermitCard } from './PermitCard';
import { AddPermitModal } from './AddPermitModal';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { usePermits, useDeletePermit, useRenewPermit, usePermitTypes } from '@/hooks';

interface PermitsListProps {
  projectId: string;
  hideHeader?: boolean; // 9.5c: Hide when wrapped in CollapsibleSection
}

export function PermitsList({ projectId, hideHeader = false }: PermitsListProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPermit, setEditingPermit] = useState<Permit | null>(null);

  // ✅ Use hooks from usePermits.ts
  const { data: permits = [], isLoading } = usePermits(projectId);
  const deletePermit = useDeletePermit();
  const renewPermit = useRenewPermit();

  // Fetch permit type definitions to resolve is_internal_milestone per permit
  const { data: permitTypesData } = usePermitTypes();
  const permitTypes = permitTypesData?.permit_types || [];

  // Split permits into actual permits vs internal milestones
  const { actualPermits, milestones } = useMemo(() => {
    const milestoneTypeIds = new Set(
      permitTypes.filter(pt => pt.is_internal_milestone).map(pt => pt.id)
    );
    const milestoneTypeNames = new Set(
      permitTypes.filter(pt => pt.is_internal_milestone).map(pt => pt.name)
    );

    const actual: Permit[] = [];
    const milestoneList: Permit[] = [];

    for (const permit of permits) {
      const isMilestone =
        (permit.permit_type_definition_id && milestoneTypeIds.has(permit.permit_type_definition_id)) ||
        milestoneTypeNames.has(permit.permit_type);
      if (isMilestone) {
        milestoneList.push(permit);
      } else {
        actual.push(permit);
      }
    }

    return { actualPermits: actual, milestones: milestoneList };
  }, [permits, permitTypes]);

  const handleEdit = (permit: Permit) => {
    setEditingPermit(permit);
    setIsModalOpen(true);
  };

  const handleDelete = async (permitId: string) => {
    if (confirm('Are you sure you want to delete this permit?')) {
      await deletePermit.mutateAsync(permitId);
    }
  };

  // Phase 8.2: Handle permit renewal
  const handleRenew = async (permit: Permit) => {
    if (confirm(`Renew this ${permit.permit_type}? This will create a new permit with the next due date.`)) {
      await renewPermit.mutateAsync({
        projectId,
        permitId: permit.id,
      });
    }
  };

  const handleAddNew = () => {
    setEditingPermit(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingPermit(null);
  };

  if (isLoading) {
    return <div>Loading permits...</div>;
  }

  return (
    <div className="space-y-4">
      {/* 9.5c: hideHeader when wrapped in CollapsibleSection */}
      {!hideHeader ? (
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Permits & Submissions ({permits.length})</h3>
          <Button onClick={handleAddNew}>
            <Plus className="h-4 w-4 mr-2" />
            Add Permit / Submission
          </Button>
        </div>
      ) : (
        <div className="flex justify-end">
          <Button onClick={handleAddNew}>
            <Plus className="h-4 w-4 mr-2" />
            Add Permit / Submission
          </Button>
        </div>
      )}

      {permits.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed rounded-lg">
          <p className="text-muted-foreground mb-4">No permits or milestones tracked yet</p>
          <p className="text-sm text-muted-foreground mb-4">
            Track building permits, development permits, issued-for-construction milestones, and more.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Actual permits */}
          {actualPermits.length > 0 && (
            <div className="space-y-2">
              {milestones.length > 0 && (
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Permits</p>
              )}
              {actualPermits.map((permit) => (
                <PermitCard
                  key={permit.id}
                  permit={permit}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onRenew={handleRenew}
                />
              ))}
            </div>
          )}

          {/* Internal milestones subsection */}
          {milestones.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Milestones</p>
              {milestones.map((permit) => (
                <PermitCard
                  key={permit.id}
                  permit={permit}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isInternalMilestone={true}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <AddPermitModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        projectId={projectId}
        editingPermit={editingPermit}
      />
    </div>
  );
}
